import { GlobalSearch } from "./GlobalSearch";
import {
  ReservationAccessDialog,
  useReservationAccess,
} from "./ReservationAccess";
import { createContext, useContext, useEffect, useId, useMemo, useRef, useState, type ReactNode, type RefObject } from "react";
import { useQuery } from "@tanstack/react-query";
import { aiApi } from "../api/ai.api";
import { schoolApi } from "../api/school.api";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  ArrowLeftToLine,
  ArrowRightToLine,
  Building2,
  BookOpen,
  CalendarClock,
  Car,
  Check,
  ChevronRight,
  ChevronsUpDown,
  Coins,
  CreditCard,
  FileText,
  GraduationCap,
  LogOut,
  Menu,
  Plus,
  Presentation,
  Settings,
  Settings2,
  ShoppingBag,
  Sparkles,
  Star,
  House,
  UserRound,
  Users,
  Wrench,
  X,
  type LucideIcon,
} from "lucide-react";
import { useAuthStore } from "../store/auth.store";
import { useMenuTheme, type MenuTheme } from "../store/appearance";
import { SubscriptionContext } from "../types";
import { SoonBadge, useFocusTrap } from "./charte";
import { StaffAvatar } from "./photos/ProfilePhotos";
import { createPortal } from "react-dom";
import "./ai-navigation.css";

// ─── Menu latéral (refonte) ───────────────────────────────────────────────────
// Dix entrées, deux niveaux au plus, un seul groupe ouvert à la fois (celui de la
// page en cours s'ouvre tout seul) : le menu reste aéré et tient dans la hauteur
// de l'écran. Mêmes pages et mêmes droits qu'avant ; seuls le rangement et le style changent.

type NavItem = {
  to: string;
  aliases?: string[];
  label: string;
  /** Rôles autorisés (par défaut : tous). */
  roles?: readonly string[];
  /** Accès RdvPermis revérifié au clic (réservation automatique) ; refusé : fenêtre de reconnexion. */
  guarded?: boolean;
};

type NavGroupDef = {
  id: string;
  label: string;
  /** Nom annoncé du sous-menu (par défaut : `label`). */
  groupLabel?: string;
  icon: LucideIcon;
  items: NavItem[];
  /** Contrôle avant ouverture (accès RdvPermis de la réservation automatique). */
  guarded?: boolean;
};

const RESERVATION_PATHS = /^\/(dashboard|requests|etps|windows|exams\/preparation)(\/|$)/;
// Pages du groupe Examens (l'ancienne Vue d'ensemble /dashboard y redirige) : le groupe s'ouvre sur elles.
const EXAM_PATHS = /^\/(exams|dashboard|requests|etps|windows)(\/|$)/;

// Refonte du menu (18/10/2026) : « Élèves » devient « Apprenants », « Moniteurs » « Enseignants » et « Équipe »
// « Outils » (libellés du menu seulement ; adresses et identifiants inchangés).
const studentsGroup: NavGroupDef = {
  id: "eleves",
  label: "Apprenants",
  icon: Users,
  items: [
    { to: "/candidates", aliases: ["/planning"], label: "Tous les apprenants" },
    { to: "/livret", label: "Livret numérique" },
  ],
};
// Examens (réorganisation du 12/10/2026) : la réservation automatique devient une fonction des examens. Un seul
// groupe : examens obtenus, demandes de réservation, seuil formateur (avec les ETP). La Vue d'ensemble
// est retirée (/dashboard → /requests). L'accès RdvPermis est revérifié au clic sur chaque entrée de réservation
// (comme l'ouverture de l'ancien groupe) et par la page elle-même ; Examens programmés reste accessible sans connexion.
// « Calendrier examens » (/slots) masqué le 08/10/2026 : réservation de places hors quota interdite ; page conservée.
// « Priorités de réservation » (/exams/preparation) quitte le menu le 19/10/2026 : onglet de Réglages › RdvPermis et réservation.
const RESERVATION_ROLES = ["owner", "manager", "agent", "read_only", "super_admin"];
const examsGroup: NavGroupDef = {
  id: "examens",
  label: "Examens",
  icon: GraduationCap,
  items: [
    { to: "/exams", label: "Examens programmés" },
    // Prévisions (19/10/2026) : prévisions des enseignants (bureau seulement) et dates d'examen demandées par les élèves
    // (la lecture seule consulte celles-ci), deux listes d'une même page ; /exams/student-requests y renvoie.
    { to: "/exams/forecasts", aliases: ["/exams/student-requests"], label: "Prévisions", roles: ["owner", "manager", "agent", "read_only", "super_admin"] },
    { to: "/requests", label: "Réservation d’examen", roles: RESERVATION_ROLES, guarded: true },
    { to: "/windows", aliases: ["/etps"], label: "Seuil formateur", roles: RESERVATION_ROLES, guarded: true },
  ],
};
const teamGroup: NavGroupDef = {
  id: "equipe",
  label: "Outils",
  icon: Wrench,
  items: [
    // Tâches : le serveur les refuse à l'enseignant (@Roles sans INSTRUCTOR) ; entrée masquée pour lui (12/10/2026).
    { to: "/workspace/tasks", label: "Tâches", roles: ["owner", "manager", "agent", "read_only", "super_admin"] },
    { to: "/workspace/notes", label: "Notes" },
    { to: "/workspace/meetings", label: "Réunions" },
    // Tableaux (/workspace/boards) retirés du menu le 19/10/2026 (demande de l'auto-école) ; la page reste ouverte
    // depuis les références (« Ajouter à un tableau ») et par son adresse.
    { to: "/workspace/sequences", label: "Séquences d’e-mails", roles: ["owner", "manager", "agent", "super_admin"] },
  ],
};
const FINANCE_ROLES = ["owner", "manager", "agent", "read_only", "super_admin"];
const financeGroup: NavGroupDef = {
  id: "finance",
  label: "Finance",
  icon: CreditCard,
  items: [
    // Facturation et encaissements : le serveur les refuse à l'enseignant (F54) ; le catalogue lui reste lisible.
    { to: "/quotes", aliases: ["/invoices", "/orders"], label: "Facturation", roles: FINANCE_ROLES },
    { to: "/payments", label: "Encaissements", roles: FINANCE_ROLES },
    { to: "/catalog", label: "Catalogue" },
  ],
};
/** Boutique Permio : entrée directe, la dernière du menu (19/10/2026, ex-Finance) ; rôles servis par la boutique. */
const SHOP_ROLES = ["owner", "manager", "agent", "super_admin"];
// Refonte du menu (18/10/2026) : plus de groupe « Structure ». Enseignants est une entrée directe, après Examens ;
// la partie Gestion = Finance, Contrats (onglets Contrats · Modèles dans la page), Outils, Véhicules, qui
// n'apparaît que si la gestion des véhicules est activée (Réglages › Équipe et accès), puis Boutique Permio, en dernier
// (19/10/2026). Agences est rangé dans Réglages › Fiche de l'auto-école.
const GROUPS = [studentsGroup, examsGroup, teamGroup, financeGroup];

// Réglages (menu du compte) : compte personnel, puis réglages de l'auto-école (une page à sections).
// Guide d'utilisation (24/10/2026) : écrans et procédures métier, ouvert au bureau (pas à l'enseignant).
const accountItems: (NavItem & { icon: LucideIcon })[] = [
  { to: "/compte", label: "Mon compte", icon: UserRound },
  { to: "/guide", label: "Guide d’utilisation", icon: BookOpen, roles: ["owner", "manager", "agent", "read_only", "super_admin"] },
];
const schoolItems: (NavItem & { icon: LucideIcon })[] = [
  { to: "/reglages", label: "Réglages de l’auto-école", icon: Settings2, roles: ["owner", "manager", "super_admin", "agent", "read_only"] },
];

const ROLE_LABELS: Record<string, string> = {
  super_admin: "Administrateur Permio",
  owner: "Gérant",
  manager: "Responsable",
  agent: "Secrétariat",
  read_only: "Lecture seule",
  instructor: "Enseignant",
};

const allowed = (item: NavItem, role: string) => !item.roles || item.roles.includes(role);

const matches = (pathname: string, path: string) =>
  pathname === path || pathname.startsWith(path + "/");

/**
 * Lien actif du menu : le chemin le plus précis l'emporte, tous groupes confondus
 * (/contracts/templates ≠ /contracts ; /exams/forecasts ≠ /exams).
 */
function activeEntry(pathname: string) {
  let best: { group: string; to: string; length: number } | null = null;
  for (const group of GROUPS)
    for (const item of group.items)
      for (const path of [item.to, ...(item.aliases ?? [])])
        if (matches(pathname, path) && (!best || path.length > best.length))
          best = { group: group.id, to: item.to, length: path.length };
  return best;
}

/** Lien actif du groupe, s'il porte la page en cours. */
function activeItemOf(group: NavGroupDef, pathname: string) {
  const entry = activeEntry(pathname);
  return entry?.group === group.id ? entry.to : null;
}

const groupOf = (pathname: string) =>
  EXAM_PATHS.test(pathname)
    ? examsGroup.id
    : (activeEntry(pathname)?.group ?? null);

// ─── Menu replié (13/10/2026) : icônes seules, nom en infobulle, groupes en menu flottant ─────────────
const Collapsed = createContext(false);
const COLLAPSE_KEY = "permio:sidebar-collapsed";
const readCollapsed = () => {
  try {
    return localStorage.getItem(COLLAPSE_KEY) === "1";
  } catch {
    return false;
  }
};

type Hint = { label: string; x: number; y: number } | null;
const HintContext = createContext<{ show: (label: string, el: HTMLElement) => void; hide: () => void }>({ show: () => {}, hide: () => {} });

/** Menu replié : nom de l'entrée en infobulle, à droite de l'icône. */
function SideHint({ label, children }: { label: string; children: ReactNode }) {
  const collapsed = useContext(Collapsed);
  const hint = useContext(HintContext);
  const ref = useRef<HTMLSpanElement>(null);
  if (!collapsed) return <>{children}</>;
  const show = () => ref.current && hint.show(label, ref.current);
  return (
    <span ref={ref} className="block" onMouseEnter={show} onMouseLeave={hint.hide} onFocus={show} onBlur={hint.hide}>
      {children}
    </span>
  );
}

/**
 * Une seule infobulle pour tout le menu replié (rendue hors du menu : la liste défilante la couperait). D'une entrée à
 * l'autre, elle se déplace sans disparaître ; elle ne s'efface qu'une fois la souris sortie des icônes.
 */
function useSideHint() {
  const [hint, setHint] = useState<Hint>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  const api = useMemo(
    () => ({
      show: (label: string, el: HTMLElement) => {
        clearTimeout(timer.current);
        const r = el.getBoundingClientRect();
        setHint({ label, x: r.right + 10, y: r.top + r.height / 2 });
      },
      hide: () => {
        clearTimeout(timer.current);
        timer.current = setTimeout(() => setHint(null), 80);
      },
    }),
    [],
  );
  useEffect(() => () => clearTimeout(timer.current), []);
  const node = hint
    ? createPortal(
        <span
          role="tooltip"
          style={{ left: hint.x, top: hint.y }}
          className="v2-root pointer-events-none fixed z-[80] -translate-y-1/2 whitespace-nowrap rounded-xl bg-heading px-3 py-2 font-sans text-xs font-medium text-white shadow-pop"
        >
          {hint.label}
        </span>,
        document.body,
      )
    : null;
  return { api, node, clear: () => setHint(null) };
}

// Aéré par défaut ; plus serré seulement sur un écran très peu haut.
const SHORT = "[@media(max-height:680px)]";
const ROW = `h-[38px] text-[14px] ${SHORT}:h-[34px] ${SHORT}:text-[13.5px]`;
const SUBROW = `h-8 ${SHORT}:h-[30px]`;

// Refonte du menu (18/10/2026), style « Épurée » (maquette menu-variantes.html, proposition 1) : aucun trait de
// séparation, recherche en pilule grise, page en cours sur fond lavande avec un trait au bord du menu, sous-pages
// alignées sur le texte avec un point pour celle en cours, aucune entrée en gras. Le mode sombre (Réglages ›
// Apparence) garde ce menu avec les couleurs de la proposition 4 (indigo profond). Les menus flottants restent blancs.
const THEMES = {
  light: {
    aside: "bg-white",
    row: "text-slate-700 hover:bg-surface-light hover:text-heading",
    icon: "text-muted group-hover:text-body",
    on: "bg-primary-50 text-primary-700",
    onIcon: "text-primary",
    mark: "before:bg-primary",
    groupIn: "text-heading hover:bg-surface-light",
    chevron: "text-muted",
    sub: "text-secondary hover:bg-surface-light hover:text-heading",
    subOn: "text-primary-700",
    label: "text-secondary",
    line: "border-surface-2",
    hover: "hover:bg-surface-light",
    fold: "text-muted hover:bg-surface-light hover:text-heading",
    idle: "text-muted",
    logoHover: "group-hover/logo:bg-surface-light group-hover/logo:text-heading",
    name: "text-heading",
    meta: "text-muted",
    fadeTop: "from-white",
    fadeBottom: "from-white",
    logo: "/logo-permio-halloween.png",
    logoO: "/logo-permio-o.png",
  },
  dark: {
    aside: "bg-gradient-to-b from-primary-900 to-[#1E1B4B]",
    row: "text-white/[.74] hover:bg-white/[.07] hover:text-white",
    icon: "text-white/50 group-hover:text-white",
    on: "bg-white/[.13] text-white shadow-[inset_0_0_0_1px_rgba(255,255,255,.08)]",
    onIcon: "text-primary-300",
    mark: "before:bg-primary-300",
    groupIn: "text-white hover:bg-white/[.07]",
    chevron: "text-white/40",
    sub: "text-white/[.62] hover:bg-white/[.07] hover:text-white",
    subOn: "text-white",
    label: "text-white/[.45]",
    line: "border-white/10",
    hover: "hover:bg-white/[.07]",
    fold: "text-white/50 hover:bg-white/[.07] hover:text-white",
    idle: "text-white/50",
    logoHover: "group-hover/logo:bg-white/[.07] group-hover/logo:text-white",
    name: "text-white",
    meta: "text-white/[.55]",
    fadeTop: "from-primary-900",
    fadeBottom: "from-[#1E1B4B]",
    // Logos aux lettres blanches (le bleu de Permio passé en blanc, le reste inchangé).
    logo: "/logo-permio-halloween-blanc.png",
    logoO: "/logo-permio-o-blanc.png",
  },
} satisfies Record<MenuTheme, Record<string, string>>;
const Theme = createContext<(typeof THEMES)[MenuTheme]>(THEMES.light);

const rowBase =
  "group relative flex w-full items-center gap-3 rounded-[10px] px-3 text-left font-normal no-underline transition-colors duration-150 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary-100";
// Trait de la page en cours, au bord gauche du menu (12 px de marge de la liste).
const BAR = "before:absolute before:-left-3 before:top-[calc(50%-9px)] before:h-[18px] before:w-[3px] before:rounded-r-[3px] before:content-['']";
// Point de la sous-page en cours, sous l'icône du groupe.
const DOT = "before:absolute before:left-5 before:top-[calc(50%-2.5px)] before:h-[5px] before:w-[5px] before:rounded-full before:content-['']";

/** Lien de premier niveau. */
function TopLink({ to, label, icon: Icon, exact = false }: { to: string; label: string; icon: LucideIcon; exact?: boolean }) {
  const { pathname, search } = useLocation();
  // Lien vers une conversation de la messagerie (`/messages?type=…`) : actif sur cette adresse seulement.
  const [path, query] = to.split("?");
  const active = query
    ? pathname === path && search === `?${query}`
    : exact
      ? pathname === to
      : matches(pathname, to);
  const collapsed = useContext(Collapsed);
  const t = useContext(Theme);
  return (
    <SideHint label={label}>
      <Link
        to={to}
        aria-current={active ? "page" : undefined}
        className={`${rowBase} ${ROW} ${active ? `${t.on} ${BAR} ${t.mark}` : t.row}`}
      >
        <Icon size={18} strokeWidth={1.8} aria-hidden className={`shrink-0 ${active ? t.onIcon : t.icon}`} />
        <span className={collapsed ? "sr-only" : "truncate"}>{label}</span>
      </Link>
    </SideHint>
  );
}

/** Groupe repliable : bouton + sous-liens. */
function NavGroup({
  group,
  role,
  open,
  busy,
  onToggle,
  onGuarded,
  onFlyout,
  folding = false,
}: {
  group: NavGroupDef;
  role: string;
  open: boolean;
  /** Fermeture du menu en cours : les sous-pages se replient en même temps que la colonne. */
  folding?: boolean;
  busy: boolean;
  onToggle: () => void;
  /** Menu replié : ouvre le menu flottant du groupe à côté de son icône. */
  onFlyout: (rect: DOMRect) => void;
  /** Entrée de réservation : contrôle de l'accès RdvPermis avant d'y aller. */
  onGuarded?: (to: string) => void;
}) {
  const { pathname } = useLocation();
  const id = useId();
  const current = activeItemOf(group, pathname);
  const inGroup = groupOf(pathname) === group.id;
  const Icon = group.icon;
  const items = group.items.filter((item) => allowed(item, role));
  // Sur un écran peu haut, le groupe ouvert se place en vue (le menu ne montre pas de barre de défilement).
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (open) requestAnimationFrame(() => ref.current?.scrollIntoView?.({ block: "nearest" }));
  }, [open]);
  const collapsed = useContext(Collapsed);
  const t = useContext(Theme);
  if (collapsed)
    return (
      <div ref={ref}>
        <SideHint label={group.label}>
          <button
            type="button"
            aria-haspopup="menu"
            aria-label={group.label}
            aria-busy={busy}
            onClick={(event) => onFlyout(event.currentTarget.getBoundingClientRect())}
            className={`${rowBase} ${ROW} ${inGroup ? `${t.on} ${BAR} ${t.mark}` : t.row}`}
          >
            <Icon size={18} strokeWidth={1.8} aria-hidden className={`shrink-0 ${inGroup ? t.onIcon : t.icon}`} />
          </button>
        </SideHint>
      </div>
    );
  return (
    <div ref={ref}>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={id}
        aria-disabled={busy}
        aria-busy={busy}
        onClick={onToggle}
        className={`${rowBase} ${ROW} ${inGroup ? t.groupIn : t.row}`}
      >
        <Icon size={18} strokeWidth={1.8} aria-hidden className={`shrink-0 ${inGroup ? t.onIcon : t.icon}`} />
        <span className="min-w-0 flex-1 truncate">{group.label}</span>
        <ChevronRight
          size={15}
          aria-hidden
          className={`shrink-0 ${t.chevron} transition-transform duration-200 ${open ? "rotate-90" : ""}`}
        />
      </button>
      <div
        id={id}
        hidden={!open}
        role="group"
        aria-label={group.groupLabel ?? group.label}
        className={`grid transition-[grid-template-rows,opacity] duration-300 ease-out ${folding ? "grid-rows-[0fr] opacity-0" : "grid-rows-[1fr]"}`}
      >
        <div className={`min-h-0 overflow-hidden ${open ? "" : "hidden"}`}>
        <div className="mb-1.5 mt-0.5 flex flex-col gap-px">
        {items.map((item) => {
          const active = current === item.to;
          return (
            <Link
              key={item.to}
              to={item.to}
              aria-current={active ? "page" : undefined}
              aria-busy={item.guarded && busy ? true : undefined}
              onClick={
                item.guarded && onGuarded
                  ? (event) => {
                      event.preventDefault();
                      onGuarded(item.to);
                    }
                  : undefined
              }
              className={`relative flex w-full items-center rounded-[10px] pl-[42px] pr-2.5 text-left text-[13px] font-normal no-underline transition-colors duration-150 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary-100 ${SUBROW} ${active ? `${t.subOn} ${DOT} ${t.mark}` : t.sub}`}
            >
              <span className="min-w-0 flex-1 truncate">{item.label}</span>
            </Link>
          );
        })}
        </div>
        </div>
      </div>
    </div>
  );
}

/** Menu replié : sous-pages d'un groupe dans un menu flottant à droite de son icône. */
function GroupFlyout({
  group,
  role,
  rect,
  onClose,
  onGuarded,
}: {
  group: NavGroupDef;
  role: string;
  rect: DOMRect;
  onClose: () => void;
  onGuarded: (to: string) => void;
}) {
  const { pathname } = useLocation();
  const current = activeItemOf(group, pathname);
  const ref = useRef<HTMLDivElement>(null);
  useDismiss(true, onClose, ref);
  useEffect(() => {
    ref.current?.querySelector<HTMLElement>("a")?.focus();
  }, []);
  const items = group.items.filter((item) => allowed(item, role));
  const top = Math.min(rect.top - 6, window.innerHeight - 24 - items.length * 36 - 44);
  return createPortal(
    <div
      ref={ref}
      role="menu"
      aria-label={group.groupLabel ?? group.label}
      style={{ left: rect.right + 10, top: Math.max(8, top) }}
      className="v2-root fixed z-[80] w-[230px] rounded-row border border-line bg-white p-1.5 font-sans tracking-[-.2px] shadow-[rgba(0,0,0,.08)_0_8px_32px_-8px,rgba(0,0,0,.05)_0_2px_6px_-2px] animate-v2-fade-in"
    >
      <p className="px-2.5 pb-1 pt-1.5 text-eyebrow font-normal uppercase text-muted">{group.label}</p>
      {items.map((item) => {
        const active = current === item.to;
        return (
          <Link
            key={item.to}
            to={item.to}
            role="menuitem"
            aria-current={active ? "page" : undefined}
            onClick={(event) => {
              if (item.guarded) {
                event.preventDefault();
                onGuarded(item.to);
              }
              onClose();
            }}
            className={`flex h-9 items-center rounded-[10px] px-2.5 text-[13px] no-underline ${active ? "bg-primary-50 font-normal text-primary-600" : "font-normal text-body hover:bg-surface-light hover:text-heading"}`}
          >
            <span className="min-w-0 flex-1 truncate">{item.label}</span>
          </Link>
        );
      })}
    </div>,
    document.body,
  );
}

/** Fermeture d'un menu flottant au clic à côté ou sur Échap (`other` : menu rendu hors de `ref`, dans la page). */
function useDismiss(open: boolean, close: () => void, ref: RefObject<HTMLElement>, other?: RefObject<HTMLElement>) {
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (ref.current && !ref.current.contains(target) && !other?.current?.contains(target)) close();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, close, ref, other]);
}

/**
 * Fenêtre « Changer d'auto-école » (ouverte depuis l'auto-école du menu du compte) : auto-écoles accessibles
 * (favoris en tête), ajout d'une auto-école (« Bientôt » : aucune fonction dans Permio) et déconnexion.
 */
function SchoolDialog({ onClose }: { onClose: () => void }) {
  const { schools, activeSchoolId, setActiveSchool, favoriteSchoolIds, toggleFavoriteSchool, logout, email } = useAuthStore((s) => ({
    schools: s.availableSchools,
    activeSchoolId: s.activeSchoolId,
    setActiveSchool: s.setActiveSchool,
    favoriteSchoolIds: s.favoriteSchoolIds,
    toggleFavoriteSchool: s.toggleFavoriteSchool,
    logout: s.logout,
    email: s.user?.email,
  }));
  const [filter, setFilter] = useState("");
  const [error, setError] = useState("");
  const [pending, setPending] = useState<string | null>(null);
  const box = useRef<HTMLDivElement>(null);
  useFocusTrap(true, box, onClose);
  const list = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return (schools ?? [])
      .filter((s) => !q || s.name.toLowerCase().includes(q))
      .sort(
        (a, b) =>
          Number(b.id === activeSchoolId) - Number(a.id === activeSchoolId) ||
          Number(favoriteSchoolIds.includes(b.id)) - Number(favoriteSchoolIds.includes(a.id)) ||
          a.name.localeCompare(b.name, "fr"),
      );
  }, [schools, filter, favoriteSchoolIds, activeSchoolId]);
  const card = "flex w-full items-center gap-3 rounded-[16px] border px-3.5 py-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary-100";
  const tile = "grid h-11 w-11 shrink-0 place-items-center rounded-[12px]";
  return createPortal(
    <div className="v2-root fixed inset-0 z-[80] grid place-items-center bg-slate-900/30 p-4 font-sans tracking-[-.2px] backdrop-blur-[2px]" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div
        ref={box}
        role="dialog"
        aria-modal="true"
        aria-labelledby="school-dialog-title"
        className="flex max-h-[min(640px,90vh)] w-full max-w-[420px] flex-col rounded-[24px] bg-white shadow-[0_24px_60px_-20px_rgba(15,23,42,0.35)] animate-v2-fade-in"
      >
        <div className="flex items-start gap-3 px-6 pb-3 pt-6">
          <div className="min-w-0 flex-1">
            <h2 id="school-dialog-title" className="text-[18px] font-semibold tracking-[-.5px] text-heading">
              Changer d’auto-école
            </h2>
            <p className="mt-0.5 text-[13px] text-secondary">Sélectionnez une auto-école ou ajoutez-en une nouvelle</p>
          </div>
          <button type="button" aria-label="Fermer" onClick={onClose} className="grid h-8 w-8 place-items-center rounded-full text-muted hover:bg-surface-light hover:text-heading">
            <X size={17} aria-hidden />
          </button>
        </div>
        <div className="flex min-h-0 flex-col gap-2 overflow-y-auto px-6 pb-6 [scrollbar-width:thin]">
          {(schools?.length ?? 0) > 6 && (
            <input
              autoFocus
              type="search"
              aria-label="Filtrer les auto-écoles"
              placeholder="Rechercher une auto-école"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="mb-1 h-10 w-full rounded-full border border-line bg-surface-light px-4 text-[13px] text-heading outline-none placeholder:text-muted focus:border-primary-300 focus:bg-white"
            />
          )}
          <ul className="m-0 flex list-none flex-col gap-2 p-0" aria-label="Auto-écoles">
            {list.map((s) => {
              const active = s.id === activeSchoolId;
              const favorite = favoriteSchoolIds.includes(s.id);
              return (
                <li key={s.id} className="relative">
                  <button
                    type="button"
                    aria-current={active ? "true" : undefined}
                    disabled={!!pending}
                    onClick={() => {
                      if (active) return onClose();
                      setPending(s.id);
                      setError("");
                      setActiveSchool(s.id).then(onClose, (e: Error) => {
                        setPending(null);
                        setError(e.message);
                      });
                    }}
                    className={`${card} pr-12 ${active ? "border-primary-200 bg-primary-50/70" : "border-line bg-white hover:border-primary-200 hover:bg-surface-light"}`}
                  >
                    <span className={`${tile} ${active ? "bg-primary text-white" : "bg-surface-light text-secondary"}`} aria-hidden>
                      <Building2 size={19} />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-1.5">
                        <span className={`truncate text-[14px] font-semibold ${active ? "text-primary-700" : "text-heading"}`}>{s.name}</span>
                        {active && <Check size={15} className="shrink-0 text-primary" aria-hidden />}
                      </span>
                      {active && email && <span className="block truncate text-[12.5px] text-secondary">{email}</span>}
                      {s.agencyName && <span className="block truncate text-[12px] text-primary-600">{s.agencyName}</span>}
                      {pending === s.id && <span className="block text-[12px] text-muted">Changement en cours…</span>}
                    </span>
                  </button>
                  <button
                    type="button"
                    aria-label={favorite ? `Retirer ${s.name} des favoris` : `Ajouter ${s.name} aux favoris`}
                    aria-pressed={favorite}
                    onClick={() => toggleFavoriteSchool(s.id)}
                    className="absolute right-3 top-1/2 grid h-8 w-8 -translate-y-1/2 place-items-center rounded-full text-muted hover:bg-white"
                  >
                    <Star size={14} aria-hidden className={favorite ? "fill-[#F0B429] text-[#F0B429]" : ""} />
                  </button>
                </li>
              );
            })}
            {list.length === 0 && <li className="px-1 py-2 text-[13px] text-muted">Aucune auto-école trouvée.</li>}
          </ul>
          {error && <p role="alert" className="px-1 text-[12.5px] text-danger">{error}</p>}
          <button type="button" disabled className={`${card} cursor-not-allowed border-dashed border-line bg-white opacity-70`}>
            <span className={`${tile} bg-surface-light text-secondary`} aria-hidden>
              <Plus size={19} />
            </span>
            <span className="min-w-0 flex-1">
              <span className="flex items-center gap-2 text-[14px] font-semibold text-heading">
                Ajouter une auto-école <SoonBadge />
              </span>
              <span className="block text-[12.5px] text-secondary">Connecter un nouveau compte</span>
            </span>
          </button>
          <button
            type="button"
            onClick={() => {
              onClose();
              void logout();
            }}
            className={`${card} border-line bg-white hover:border-red-200 hover:bg-red-50/60`}
          >
            <span className={`${tile} bg-surface-light text-secondary`} aria-hidden>
              <LogOut size={19} />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-[14px] font-semibold text-heading">Se déconnecter</span>
              <span className="block text-[12.5px] text-secondary">Quitter votre compte</span>
            </span>
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/**
 * Pied du menu : compte, auto-école active et réglages. Le changement d'auto-école est ici :
 * il n'existait que dans l'ancienne barre du haut, masquée sur les pages refaites.
 */
function AccountMenu() {
  const collapsed = useContext(Collapsed);
  const t = useContext(Theme);
  const { user, school, schools, activeSchoolId } = useAuthStore((s) => ({
    user: s.user,
    school: s.school,
    schools: s.availableSchools,
    activeSchoolId: s.activeSchoolId,
  }));
  const [open, setOpen] = useState(false);
  const [schoolDialog, setSchoolDialog] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const { pathname } = useLocation();
  const close = () => setOpen(false);
  useDismiss(open, close, ref, menuRef);
  useEffect(close, [pathname]);
  // Replié, le menu est rendu à la fin de la page : on y place le clavier, comme dans GroupFlyout.
  useEffect(() => {
    if (open && collapsed) menuRef.current?.querySelector<HTMLElement>("button, a")?.focus();
  }, [open, collapsed]);
  const role = user?.role ?? "";
  const name = [user?.firstName, user?.lastName].filter(Boolean).join(" ") || user?.email || "Mon compte";
  const list = schools ?? [];
  const schoolName = school?.name ?? list.find((s) => s.id === activeSchoolId)?.name ?? "Auto-école";
  const item = (i: NavItem & { icon?: LucideIcon }) => (
    <Link
      key={i.to}
      to={i.to}
      onClick={close}
      className="group flex h-9 items-center gap-2.5 rounded-[10px] px-2.5 text-[13px] font-medium text-body no-underline hover:bg-primary-50 hover:text-primary-600"
    >
      {i.icon && <i.icon size={16} strokeWidth={1.8} aria-hidden className="shrink-0 text-muted group-hover:text-primary" />}
      {i.label}
    </Link>
  );
  // Budget Permio AI du jour (même lecture que Réglages › Permio AI), pour le gérant et le responsable.
  const canSeeAi = ["owner", "manager"].includes(role);
  const ai = useQuery({
    queryKey: ["ai-usage", activeSchoolId, user?.id, role],
    queryFn: aiApi.usage,
    enabled: open && canSeeAi && !!activeSchoolId,
    retry: false,
    staleTime: 60_000,
  });
  const aiLeft =
    ai.data?.budgetMicroUsd != null && ai.data.budgetMicroUsd > 0 && ai.data.remainingMicroUsd != null
      ? Math.min(100, Math.max(0, Math.floor((100 * ai.data.remainingMicroUsd) / ai.data.budgetMicroUsd)))
      : null;
  // Replié, le menu déborde sur la page : rendu hors du menu (comme GroupFlyout), sinon les blocs de la page
  // placés au-dessus (accueil : .hm-dash, z-index 2) le recouvrent, le menu latéral collant formant sa propre couche.
  const menu = open && (
    <div
      ref={menuRef}
      id="sidebar-settings"
      className={`${collapsed ? "v2-root fixed bottom-3 left-[74px] z-[80] w-[280px] font-sans tracking-[-.2px]" : "absolute bottom-full left-3 right-3 z-50 mb-1"} rounded-row border border-line bg-white p-1.5 shadow-[rgba(0,0,0,.08)_0_-8px_32px_-8px,rgba(0,0,0,.05)_0_2px_6px_-2px] animate-v2-fade-in`}
    >
      <>
          {/* Auto-école active : ouvre la fenêtre de changement d'auto-école (et la déconnexion). */}
          <button
            type="button"
            onClick={() => {
              close();
              setSchoolDialog(true);
            }}
            aria-haspopup="dialog"
            className="mb-1 flex w-full items-center gap-2.5 rounded-[12px] border border-line px-2.5 py-2 text-left hover:border-primary-200 hover:bg-surface-light"
          >
            <span className="grid h-8 w-8 shrink-0 place-items-center rounded-[9px] bg-surface-light text-secondary" aria-hidden>
              <Building2 size={15} />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[13px] font-semibold text-heading">{schoolName}</span>
              <span className="block truncate text-[11.5px] text-secondary">{user?.email}</span>
            </span>
            <ChevronsUpDown size={15} className="shrink-0 text-muted" aria-hidden />
          </button>
          {accountItems.filter((i) => allowed(i, role)).map(item)}
          {schoolItems.filter((i) => allowed(i, role)).map(item)}
          {canSeeAi && (
            <Link
              to="/reglages/ia"
              onClick={close}
              aria-label={aiLeft == null ? "Crédit Permio AI" : `Crédit Permio AI : ${aiLeft} % restant aujourd’hui`}
              className="mt-1 flex h-9 items-center gap-2.5 rounded-[10px] bg-amber-50/70 px-2.5 text-[13px] font-medium text-amber-900 no-underline hover:bg-amber-100"
            >
              <Coins size={16} strokeWidth={1.8} aria-hidden className="shrink-0 text-amber-500" />
              <span className="min-w-0 flex-1 truncate">Crédit Permio AI</span>
              <span className="shrink-0 font-semibold tabular-nums">
                {ai.isLoading ? "…" : aiLeft == null ? "—" : `${aiLeft} %`}
              </span>
            </Link>
          )}
      </>
    </div>
  );
  return (
    <div ref={ref} className={`relative shrink-0 border-t ${t.line} px-3 py-2.5`}>
      {menu && (collapsed ? createPortal(menu, document.body) : menu)}
      {schoolDialog && <SchoolDialog onClose={() => setSchoolDialog(false)} />}
      <button
        type="button"
        aria-expanded={open}
        aria-controls="sidebar-settings"
        onClick={() => (open ? close() : setOpen(true))}
        title={collapsed ? `${name} · ${schoolName}` : undefined}
        className={`group flex w-full items-center gap-2.5 rounded-row py-1.5 text-left transition-colors ${t.hover} focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary-100 px-2`}
      >
        <StaffAvatar userId={user?.id} name={name} size="sm" />
        <span className={collapsed ? "sr-only" : "min-w-0 flex-1"}>
          <span className={`block truncate text-[13px] font-normal ${t.name}`}>{name}</span>
          <span className={`block truncate text-[11px] ${t.meta}`}>{schoolName}</span>
        </span>
        {!collapsed && <Settings size={16} aria-hidden className={`shrink-0 ${t.icon} transition-transform duration-300 group-hover:rotate-90`} />}
        <span className="sr-only">
          {ROLE_LABELS[role] ? `${ROLE_LABELS[role]}, ` : ""}Réglages
        </span>
      </button>
    </div>
  );
}

export function Sidebar({ subContext }: { subContext?: SubscriptionContext }) {
  void subContext; // conservé pour compat ProtectedLayout (gating réactivable plus tard)
  const role = useAuthStore((state) => state.user?.role) ?? "";
  // Même lecture que Réglages (cache vidé au changement d'école) : un réglage changé met le menu à jour.
  const settings = useQuery({ queryKey: ["school-settings"], queryFn: () => schoolApi.fetchSettings(), staleTime: 60_000, retry: false });
  const vehiclesEnabled = settings.data?.settings?.vehiclesEnabled === true;
  const [menuTheme] = useMenuTheme();
  const t = THEMES[menuTheme];

  const navigate = useNavigate();
  const location = useLocation();
  const { status: reservationStatus, check: checkReservationToken } =
    useReservationAccess();
  const [reservationPrompt, setReservationPrompt] = useState(false);
  const activeSchoolId = useAuthStore((s) => s.activeSchoolId);
  const reservationRequest = useRef(0);
  function closeReservationPrompt() {
    reservationRequest.current++;
    setReservationPrompt(false);
  }
  useEffect(() => {
    reservationRequest.current++;
    setReservationPrompt(false);
  }, [activeSchoolId]);
  async function checkReservation() {
    const request = ++reservationRequest.current;
    const allowedAccess = await checkReservationToken();
    if (request !== reservationRequest.current) return false;
    setReservationPrompt(!allowedAccess);
    if (!allowedAccess && RESERVATION_PATHS.test(location.pathname))
      navigate("/lessons", { replace: true });
    return allowedAccess;
  }

  // Un seul groupe ouvert ; celui de la page en cours s'ouvre à l'arrivée.
  const currentGroup = groupOf(location.pathname);
  const [openGroup, setOpenGroup] = useState<string | null>(currentGroup);
  useEffect(() => {
    if (currentGroup) setOpenGroup(currentGroup);
  }, [location.pathname, currentGroup]);
  const [checking, setChecking] = useState(false);
  async function toggleGroup(group: NavGroupDef) {
    if (checking) return;
    // Réservation automatique : accès RdvPermis revérifié à chaque clic (comme avant),
    // un accès expiré referme le groupe et ouvre la reconnexion.
    if (group.guarded) {
      setChecking(true);
      const ok = await checkReservation();
      setChecking(false);
      if (!ok) {
        setOpenGroup((current) => (current === group.id ? null : current));
        return;
      }
    }
    setOpenGroup((current) => (current === group.id ? null : group.id));
  }

  // Entrée de réservation : accès RdvPermis revérifié à chaque clic ; expiré, la fenêtre de reconnexion s'ouvre.
  async function openGuarded(to: string) {
    if (checking) return;
    setChecking(true);
    const ok = await checkReservation();
    setChecking(false);
    if (ok) navigate(to);
  }

  // Fondus haut / bas si la liste dépasse la hauteur disponible (écrans très peu hauts).
  const navRef = useRef<HTMLElement>(null);
  const [fade, setFade] = useState({ top: false, bottom: false });
  const measureFade = () => {
    const el = navRef.current;
    if (!el) return;
    const next = { top: el.scrollTop > 2, bottom: el.scrollTop + el.clientHeight < el.scrollHeight - 2 };
    setFade((prev) => (prev.top === next.top && prev.bottom === next.bottom ? prev : next));
  };
  useEffect(() => {
    const el = navRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measureFade);
    observer.observe(el);
    Array.from(el.children).forEach((child) => observer.observe(child));
    return () => observer.disconnect();
  }, []);
  useEffect(measureFade, [openGroup]);

  // Téléphone et tablette : menu en panneau glissant, refermé à chaque navigation.
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => setMobileOpen(false), [location.pathname]);

  // Ordinateur : menu repliable (icônes seules), choix mémorisé sur ce navigateur. Le panneau téléphone reste déplié.
  const [collapsedPref, setCollapsedPref] = useState(readCollapsed);
  // Fermeture en douceur : la colonne coulisse d'abord (transition CSS), le contenu passe en icônes seules ensuite.
  // À l'ouverture, le contenu complet revient tout de suite et se dévoile pendant que la colonne s'élargit.
  const [compact, setCompact] = useState(collapsedPref);
  useEffect(() => {
    if (!collapsedPref) return setCompact(false);
    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const timer = setTimeout(() => setCompact(true), reduced ? 0 : 280);
    return () => clearTimeout(timer);
  }, [collapsedPref]);
  const narrow = collapsedPref && !mobileOpen;
  const collapsed = narrow && compact;
  const [flyout, setFlyout] = useState<{ id: string; rect: DOMRect } | null>(null);
  const sideHint = useSideHint();
  useEffect(() => {
    setFlyout(null);
    sideHint.clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, collapsedPref]);
  const setCollapsed = (value: boolean) => {
    setCollapsedPref(value);
    try {
      localStorage.setItem(COLLAPSE_KEY, value ? "1" : "0");
    } catch {
      /* stockage indisponible : le choix vaut pour la session */
    }
  };
  async function openFlyout(group: NavGroupDef, rect: DOMRect) {
    if (checking) return;
    if (flyout?.id === group.id) return setFlyout(null);
    // Même contrôle d'accès RdvPermis qu'à l'ouverture du groupe dans le menu déplié.
    if (group.guarded) {
      setChecking(true);
      const ok = await checkReservation();
      setChecking(false);
      if (!ok) return;
    }
    setFlyout({ id: group.id, rect });
  }

  const renderGroup = (g: NavGroupDef) => (
    <NavGroup
      key={g.id}
      group={g}
      role={role}
      open={openGroup === g.id}
      folding={narrow}
      busy={g.guarded || g.items.some((i) => i.guarded) ? checking : false}
      onToggle={() => void toggleGroup(g)}
      onGuarded={(to) => void openGuarded(to)}
      onFlyout={(rect) => void openFlyout(g, rect)}
    />
  );
  const flyoutGroup = flyout ? GROUPS.find((g) => g.id === flyout.id) : undefined;

  return (
    <>
      <button
        type="button"
        className="app-mobile-menu"
        aria-expanded={mobileOpen}
        aria-controls="app-sidebar"
        aria-label={mobileOpen ? "Fermer le menu" : "Menu Permio"}
        onClick={() => setMobileOpen((open) => !open)}
      >
        {mobileOpen ? <X size={18} aria-hidden /> : <Menu size={18} aria-hidden />}
        <span>{mobileOpen ? "Fermer" : "Menu"}</span>
        <img src="/logo-permio-halloween.png" alt="" className="app-mobile-menu__logo" />
      </button>
      {mobileOpen && <div className="app-mobile-backdrop" aria-hidden onClick={() => setMobileOpen(false)} />}
      <Collapsed.Provider value={collapsed}>
      <HintContext.Provider value={sideHint.api}>
      <Theme.Provider value={t}>
      {/* Pas de bordure à droite : le contenu de la page porte déjà le filet (.app-shell__content). */}
      <aside
        id="app-sidebar"
        data-mobile-open={mobileOpen}
        data-collapsed={narrow}
        data-theme={menuTheme}
        data-testid="sidebar"
        className={`v2-root sticky top-0 flex h-screen w-full flex-col overflow-x-hidden font-sans tracking-[-.2px] ${t.aside}`}
      >
        {reservationPrompt && (
          <ReservationAccessDialog
            status={reservationStatus}
            retry={() => void checkReservation()}
            close={closeReservationPrompt}
          />
        )}
        {/* En-tête (façon lemlist) : logo à gauche et flèche pour replier ; replié, le « O » de Permio et la flèche dessous. */}
        {collapsed ? (
          <div className="app-sidebar-logo flex h-[68px] shrink-0">
            {/* Toute la zone du logo est cliquable : au survol, le « O » de Permio devient la flèche d'ouverture. */}
            <button
              type="button"
              aria-label="Agrandir le menu"
              onClick={() => setCollapsed(false)}
              className={`group/logo grid h-full w-full place-items-center ${t.idle} focus-visible:outline-none`}
            >
              <span className={`grid h-10 w-10 place-items-center rounded-[12px] transition-colors ${t.logoHover} group-focus-visible/logo:ring-4 group-focus-visible/logo:ring-primary-100`}>
                <img src={t.logoO} alt="Permio" className="h-9 w-9 animate-v2-fade-in group-hover/logo:hidden group-focus-visible/logo:hidden" />
                <ArrowRightToLine size={18} strokeWidth={1.9} aria-hidden className="hidden group-hover/logo:block group-focus-visible/logo:block" />
              </span>
            </button>
          </div>
        ) : (
          <div className="app-sidebar-logo flex h-[68px] shrink-0 items-center justify-between gap-2 pl-5 pr-3">
            <img src={t.logo} alt="Permio" className="h-9 w-auto" />
            <button
              type="button"
              aria-label="Réduire le menu"
              title="Réduire le menu"
              onClick={() => setCollapsed(true)}
              className={`grid h-8 w-8 place-items-center rounded-[10px] ${t.fold} transition-colors focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary-100`}
            >
              <ArrowLeftToLine size={17} strokeWidth={1.9} aria-hidden />
            </button>
          </div>
        )}
        <div className="app-sidebar-head shrink-0 px-3 pb-3 pt-1">
          <SideHint label="Rechercher (Ctrl K)">
            <GlobalSearch />
          </SideHint>
        </div>

        <div className="relative flex min-h-0 flex-1 flex-col">
          <nav
            ref={navRef}
            onScroll={measureFade}
            aria-label="Navigation principale"
            className={`min-h-0 flex-1 overflow-y-auto pb-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden px-3`}
          >
            <section aria-labelledby="navigation-daily" className="flex flex-col gap-1">
              <h2 id="navigation-daily" className="sr-only">Quotidien</h2>
              <TopLink to="/today" label="Accueil" icon={House} />
              {["owner", "manager", "agent", "read_only"].includes(role) && (
                <TopLink to="/messages?type=assistant" label="Permio AI" icon={Sparkles} />
              )}
              <TopLink to="/lessons" label="Planning" icon={CalendarClock} />
              <div aria-hidden className={`h-3 ${SHORT}:h-1`} />
              {renderGroup(studentsGroup)}
              {renderGroup(examsGroup)}
              <TopLink to="/instructors" label="Enseignants" icon={Presentation} />
            </section>
            {/* Gestion : un titre discret, sans trait ; replié, un filet le remplace. */}
            <section
              aria-labelledby="navigation-management"
              className={`mt-4 flex flex-col gap-1 ${SHORT}:mt-2 ${collapsed ? `border-t ${t.line} pt-3.5 ${SHORT}:pt-2` : ""}`}
            >
              <h2
                id="navigation-management"
                className={collapsed ? "sr-only" : `overflow-hidden whitespace-nowrap px-3 pb-1.5 pt-0.5 text-[12px] font-normal ${t.label} transition-opacity duration-150 ${narrow ? "opacity-0" : ""}`}
              >
                Gestion
              </h2>
              {renderGroup(financeGroup)}
              <TopLink to="/contracts" label="Contrats" icon={FileText} />
              {renderGroup(teamGroup)}
              {vehiclesEnabled && <TopLink to="/fleet" label="Véhicules" icon={Car} />}
              {SHOP_ROLES.includes(role) && <TopLink to="/boutique-permio" label="Boutique Permio" icon={ShoppingBag} />}
            </section>
          </nav>
          {/* Fondu discret quand des entrées restent sous le bord. */}
          {fade.top && <div aria-hidden className={`pointer-events-none absolute inset-x-0 top-0 h-6 bg-gradient-to-b ${t.fadeTop} to-transparent`} />}
          {fade.bottom && <div aria-hidden className={`pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t ${t.fadeBottom} to-transparent`} />}
        </div>

        <AccountMenu />
      </aside>
      {flyoutGroup && flyout && (
        <GroupFlyout group={flyoutGroup} role={role} rect={flyout.rect} onClose={() => setFlyout(null)} onGuarded={(to) => void openGuarded(to)} />
      )}
      {collapsed && !flyout && sideHint.node}
      </Theme.Provider>
      </HintContext.Provider>
      </Collapsed.Provider>
    </>
  );
}
